import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ResponsiveTabsList, ResponsiveTabsTrigger } from "@/components/admin/ResponsiveTabsComponents";
import { 
  Activity, 
  GitBranch, 
  MessageSquare, 
  BarChart3, 
  Heart,
  Info,
  TestTube,
  Tag
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { StatusManager } from "./status/StatusManager";
import { FlowsManager } from "./flows/FlowsManager";
import { CommunicationManager } from "./communication/CommunicationManager";
import { HealthManager } from "./health/HealthManager";
import { FlowTestingPanel } from "./flows/FlowTestingPanel";
import { EventCategoriesManager } from "./categories/EventCategoriesManager";

export function EventsManager() {
  const [activeTab, setActiveTab] = useState("categories");
  const isMobile = useIsMobile();


  const eventTabs = [
    { value: "categories", label: "Event Categories", mobileLabel: "Categories", icon: Tag },
    { value: "status", label: "Status Management", mobileLabel: "Status", icon: Activity },
    { value: "flows", label: "Event Flows", mobileLabel: "Flows", icon: GitBranch },
    { value: "testing", label: "Event Testing", mobileLabel: "Testing", icon: TestTube },
    { value: "communication", label: "Communication", mobileLabel: "Comm", icon: MessageSquare },
    { value: "monitoring", label: "Monitoring", mobileLabel: "Monitor", icon: BarChart3 },
    { value: "health", label: "System Health", mobileLabel: "Health", icon: Heart }
  ];

  return (
    <div className="bg-white rounded-lg border shadow-sm space-y-4 sm:space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Event Orchestration</h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          Manage booking status flows, event triggers, and automated communications
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
        {/* Mobile: Dropdown Navigation */}
        {isMobile ? (
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full h-12">
              <div className="flex items-center space-x-2">
                {(() => {
                  const currentTab = eventTabs.find(tab => tab.value === activeTab);
                  const Icon = currentTab?.icon;
                  return (
                    <>
                      {Icon && <Icon className="h-4 w-4" />}
                      <span>{currentTab?.label}</span>
                    </>
                  );
                })()}
              </div>
            </SelectTrigger>
            <SelectContent className="z-50">
              {eventTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <SelectItem key={tab.value} value={tab.value}>
                    <div className="flex items-center space-x-2">
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        ) : (
          // Desktop: Tab Navigation with responsive layout
          <ResponsiveTabsList enableScrolling={eventTabs.length > 3}>
            {eventTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <ResponsiveTabsTrigger
                  key={tab.value}
                  value={tab.value}
                  icon={<Icon className="h-4 w-4" />}
                  label={tab.label}
                  mobileLabel={tab.mobileLabel}
                />
              );
            })}
          </ResponsiveTabsList>
        )}

        <TabsContent value="categories" className="space-y-4">
          <EventCategoriesManager />
        </TabsContent>

        <TabsContent value="status" className="space-y-4">
          <StatusManager />
        </TabsContent>

        <TabsContent value="flows" className="space-y-4">
          <FlowsManager />
        </TabsContent>

        <TabsContent value="testing" className="space-y-4">
          <TooltipProvider>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                      <TestTube className="h-5 w-5" />
                      Event Testing
                    </CardTitle>
                    <CardDescription className="text-sm">
                      Test the complete event orchestration pipeline from status transitions to communication delivery
                    </CardDescription>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <Info className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-md p-4" side="left">
                      <div className="space-y-2">
                        <h4 className="font-semibold">End-to-End Event Testing</h4>
                        <p className="text-sm">This testing environment allows you to:</p>
                        <ul className="text-sm space-y-1 list-disc pl-4">
                          <li><strong>Simulate Status Changes:</strong> Test how status transitions trigger event flows</li>
                          <li><strong>Validate Event Flows:</strong> Ensure configured flows match the correct transitions</li>
                          <li><strong>Test Communications:</strong> Verify that emails, SMS, and webhooks are properly triggered</li>
                          <li><strong>Monitor Queue Processing:</strong> See how events are queued and processed</li>
                        </ul>
                        <p className="text-sm pt-2">Use this to validate your entire event orchestration pipeline before deploying changes.</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </CardHeader>
              <CardContent>
                <FlowTestingPanel />
              </CardContent>
            </Card>
          </TooltipProvider>
        </TabsContent>

        <TabsContent value="communication" className="space-y-4">
          <CommunicationManager />
        </TabsContent>

        <TabsContent value="monitoring" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <BarChart3 className="h-5 w-5" />
                Event Monitoring
              </CardTitle>
              <CardDescription className="text-sm">
                Monitor event queue and orchestration logs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center text-muted-foreground py-8">
                <BarChart3 className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm sm:text-base">Event Monitoring coming soon</p>
                <p className="text-xs sm:text-sm">This will include queue management and delivery logs</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="health" className="space-y-4">
          <HealthManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}